// frontend/src/components/organizations/OrganizationModal.tsx
// Standalone OrganizationModal component with comprehensive form handling

import React, { useState, useEffect, useCallback, memo } from 'react';
import { cn } from '@/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { 
  Organization,
  OrganizationCreateRequest,
  OrganizationUpdateRequest
} from '@/api/types';

// ===== INTERFACES =====

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization?: Organization;
  onSave: (data: OrganizationCreateRequest | OrganizationUpdateRequest) => void;
  loading?: boolean;
}

interface FormData {
  // Basic info
  name: string;
  description: string;
  
  // Resource limits
  maxUsers: number;
  maxProjects: number;
  maxGroups: number;
  maxWorkflows: number;
  
  // Email management settings
  maxEmailAccounts: number;
  emailRetentionDays: number;
  maxEmailStorage: number;
  maxCorrespondenceStorage: number;
  maxTotalStorage: number;
  emergencyPreservationEnabled: boolean;
  
  // Storage and correspondence limits
  maxCorrespondences: number;
  
  // Subscription and plan settings
  plan: 'basic' | 'standard' | 'enterprise';
  isAIEnabled: boolean;
  subscribedModules: string[];
  
  // Organization status
  isActive: boolean;
  
  // Admin user creation (for new orgs)
  adminUser: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  };
  
  // Email lifecycle and correspondence policies
  correspondencePolicy: {
    autoClassificationEnabled: boolean;
    requireManualReview: boolean;
    preservationPriority: 'high' | 'medium' | 'low';
    maxAttachmentSize: number;
  };
}

type TabType = 'basic' | 'resources' | 'email' | 'admin' | 'policies';

// ===== CONSTANTS =====

const DEFAULT_FORM_DATA: FormData = {
  name: '',
  description: '',
  maxUsers: 100,
  maxProjects: 50,
  maxGroups: 25,
  maxWorkflows: 10,
  maxEmailAccounts: 10,
  emailRetentionDays: 365,
  maxEmailStorage: 1000000000, // 1GB in bytes
  maxCorrespondenceStorage: 1000000000, // 1GB in bytes
  maxTotalStorage: 2000000000, // 2GB in bytes
  emergencyPreservationEnabled: false,
  maxCorrespondences: 10000,
  plan: 'basic',
  isAIEnabled: false,
  subscribedModules: [],
  isActive: true,
  adminUser: {
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: ''
  },
  correspondencePolicy: {
    autoClassificationEnabled: true,
    requireManualReview: false,
    preservationPriority: 'medium',
    maxAttachmentSize: 25000000 // 25MB in bytes
  }
};

// ===== UTILITY FUNCTIONS =====

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const parseStorageValue = (value: string): number => {
  const num = parseFloat(value);
  if (isNaN(num)) return 0;
  
  // Convert to bytes based on unit
  if (value.includes('GB') || value.includes('gb')) {
    return num * 1024 * 1024 * 1024;
  } else if (value.includes('MB') || value.includes('mb')) {
    return num * 1024 * 1024;
  } else if (value.includes('KB') || value.includes('kb')) {
    return num * 1024;
  }
  return num;
};

// ===== VALIDATION FUNCTIONS =====

const validateForm = (formData: FormData, isEditing: boolean): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Basic validation
  if (!formData.name.trim()) {
    errors.name = 'Organization name is required';
  } else if (formData.name.length < 2) {
    errors.name = 'Organization name must be at least 2 characters';
  } else if (formData.name.length > 100) {
    errors.name = 'Organization name must be less than 100 characters';
  }

  if (formData.description && formData.description.length > 500) {
    errors.description = 'Description must be less than 500 characters';
  }

  // Resource limits validation (matching backend validation)
  if (formData.maxUsers < 1 || formData.maxUsers > 1000) {
    errors.maxUsers = 'Max users must be between 1 and 1000';
  }
  if (formData.maxProjects < 1 || formData.maxProjects > 200) {
    errors.maxProjects = 'Max projects must be between 1 and 200';
  }
  if (formData.maxGroups < 1 || formData.maxGroups > 100) {
    errors.maxGroups = 'Max groups must be between 1 and 100';
  }
  if (formData.maxWorkflows < 1 || formData.maxWorkflows > 50) {
    errors.maxWorkflows = 'Max workflows must be between 1 and 50';
  }

  // Email settings validation
  if (formData.maxEmailAccounts < 1 || formData.maxEmailAccounts > 100) {
    errors.maxEmailAccounts = 'Max email accounts must be between 1 and 100';
  }
  if (formData.emailRetentionDays < 1 || formData.emailRetentionDays > 2555) {
    errors.emailRetentionDays = 'Email retention days must be between 1 and 2555';
  }
  if (formData.maxEmailStorage < 100000000) { // min 100MB
    errors.maxEmailStorage = 'Max email storage must be at least 100MB';
  }
  if (formData.maxCorrespondenceStorage < 100000000) { // min 100MB
    errors.maxCorrespondenceStorage = 'Max correspondence storage must be at least 100MB';
  }
  if (formData.maxCorrespondences < 1 || formData.maxCorrespondences > 1000000) {
    errors.maxCorrespondences = 'Max correspondences must be between 1 and 1,000,000';
  }

  // Admin user validation (only for new organizations)
  if (!isEditing) {
    if (!formData.adminUser.username.trim()) {
      errors['adminUser.username'] = 'Admin username is required';
    } else if (!/^[a-zA-Z0-9._-]{3,30}$/.test(formData.adminUser.username)) {
      errors['adminUser.username'] = 'Username must be 3-30 characters and contain only letters, numbers, periods, underscores, and hyphens';
    }

    if (!formData.adminUser.email.trim()) {
      errors['adminUser.email'] = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminUser.email)) {
      errors['adminUser.email'] = 'Valid email address is required';
    }

    if (!formData.adminUser.firstName.trim()) {
      errors['adminUser.firstName'] = 'First name is required';
    }

    if (!formData.adminUser.lastName.trim()) {
      errors['adminUser.lastName'] = 'Last name is required';
    }

    if (!formData.adminUser.password) {
      errors['adminUser.password'] = 'Password is required';
    } else if (formData.adminUser.password.length < 8) {
      errors['adminUser.password'] = 'Password must be at least 8 characters';
    }
  }

  // Correspondence policy validation
  if (formData.correspondencePolicy.maxAttachmentSize < 1000000) { // min 1MB
    errors['correspondencePolicy.maxAttachmentSize'] = 'Max attachment size must be at least 1MB';
  }

  return errors;
};

// ===== TAB COMPONENTS =====

const BasicInfoTab: React.FC<{
  formData: FormData;
  errors: Record<string, string>;
  onInputChange: (field: string, value: any) => void;
}> = ({ formData, errors, onInputChange }) => (
  <div className="p-6 space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <Input
          label="Organization Name *"
          value={formData.name}
          onChange={(e) => onInputChange('name', e.target.value)}
          error={errors.name}
          placeholder="Enter organization name"
        />
      </div>

      <div>
        <label htmlFor="org-plan" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Subscription Plan
        </label>
        <select
          id="org-plan"
          value={formData.plan}
          onChange={(e) => onInputChange('plan', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="basic">Basic</option>
          <option value="standard">Standard</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>
    </div>

    <div>
      <Textarea
        label="Description"
        value={formData.description}
        onChange={(e) => onInputChange('description', e.target.value)}
        error={errors.description}
        placeholder="Enter organization description"
        rows={3}
      />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="flex items-center">
        <input
          id="ai-enabled"
          type="checkbox"
          checked={formData.isAIEnabled}
          onChange={(e) => onInputChange('isAIEnabled', e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="ai-enabled" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
          Enable AI Features
        </label>
      </div>

      <div className="flex items-center">
        <input
          id="org-active"
          type="checkbox"
          checked={formData.isActive}
          onChange={(e) => onInputChange('isActive', e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="org-active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
          Organization Active
        </label>
      </div>
    </div>
  </div>
);

const ResourceLimitsTab: React.FC<{
  formData: FormData;
  errors: Record<string, string>;
  onInputChange: (field: string, value: any) => void;
}> = ({ formData, errors, onInputChange }) => (
  <div className="p-6 space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Input
        label="Max Users (1-1000)"
        type="number"
        min="1"
        max="1000"
        value={formData.maxUsers}
        onChange={(e) => onInputChange('maxUsers', parseInt(e.target.value) || 1)}
        error={errors.maxUsers}
      />

      <Input
        label="Max Projects (1-200)"
        type="number"
        min="1"
        max="200"
        value={formData.maxProjects}
        onChange={(e) => onInputChange('maxProjects', parseInt(e.target.value) || 1)}
        error={errors.maxProjects}
      />

      <Input
        label="Max Groups (1-100)"
        type="number"
        min="1"
        max="100"
        value={formData.maxGroups}
        onChange={(e) => onInputChange('maxGroups', parseInt(e.target.value) || 1)}
        error={errors.maxGroups}
      />

      <Input
        label="Max Workflows (1-50)"
        type="number"
        min="1"
        max="50"
        value={formData.maxWorkflows}
        onChange={(e) => onInputChange('maxWorkflows', parseInt(e.target.value) || 1)}
        error={errors.maxWorkflows}
      />

      <Input
        label="Max Correspondences (1-1,000,000)"
        type="number"
        min="1"
        max="1000000"
        value={formData.maxCorrespondences}
        onChange={(e) => onInputChange('maxCorrespondences', parseInt(e.target.value) || 1)}
        error={errors.maxCorrespondences}
      />
    </div>
  </div>
);

const EmailSettingsTab: React.FC<{
  formData: FormData;
  errors: Record<string, string>;
  onInputChange: (field: string, value: any) => void;
}> = ({ formData, errors, onInputChange }) => (
  <div className="p-6 space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Input
        label="Max Email Accounts (1-100)"
        type="number"
        min="1"
        max="100"
        value={formData.maxEmailAccounts}
        onChange={(e) => onInputChange('maxEmailAccounts', parseInt(e.target.value) || 1)}
        error={errors.maxEmailAccounts}
      />

      <Input
        label="Email Retention Days (1-2555)"
        type="number"
        min="1"
        max="2555"
        value={formData.emailRetentionDays}
        onChange={(e) => onInputChange('emailRetentionDays', parseInt(e.target.value) || 1)}
        error={errors.emailRetentionDays}
      />

      <Input
        label="Max Email Storage"
        type="text"
        value={formatBytes(formData.maxEmailStorage)}
        onChange={(e) => {
          const bytes = parseStorageValue(e.target.value);
          onInputChange('maxEmailStorage', bytes);
        }}
        error={errors.maxEmailStorage}
        placeholder="e.g., 1 GB"
      />

      <Input
        label="Max Correspondence Storage"
        type="text"
        value={formatBytes(formData.maxCorrespondenceStorage)}
        onChange={(e) => {
          const bytes = parseStorageValue(e.target.value);
          onInputChange('maxCorrespondenceStorage', bytes);
        }}
        error={errors.maxCorrespondenceStorage}
        placeholder="e.g., 1 GB"
      />

      <div className="md:col-span-2">
        <div className="flex items-center">
          <input
            id="emergency-preservation"
            type="checkbox"
            checked={formData.emergencyPreservationEnabled}
            onChange={(e) => onInputChange('emergencyPreservationEnabled', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="emergency-preservation" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
            Enable Emergency Email Preservation
          </label>
        </div>
      </div>
    </div>
  </div>
);

const AdminUserTab: React.FC<{
  formData: FormData;
  errors: Record<string, string>;
  onInputChange: (field: string, value: any) => void;
}> = ({ formData, errors, onInputChange }) => (
  <div className="p-6 space-y-6">
    <div className="border-l-4 border-blue-500 pl-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Create Admin User</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        This user will be created as the organization administrator with full access.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Input
        label="Username *"
        value={formData.adminUser.username}
        onChange={(e) => onInputChange('adminUser.username', e.target.value)}
        error={errors['adminUser.username']}
        placeholder="admin username"
      />

      <Input
        label="Email *"
        type="email"
        value={formData.adminUser.email}
        onChange={(e) => onInputChange('adminUser.email', e.target.value)}
        error={errors['adminUser.email']}
        placeholder="admin@example.com"
      />

      <Input
        label="First Name *"
        value={formData.adminUser.firstName}
        onChange={(e) => onInputChange('adminUser.firstName', e.target.value)}
        error={errors['adminUser.firstName']}
        placeholder="First name"
      />

      <Input
        label="Last Name *"
        value={formData.adminUser.lastName}
        onChange={(e) => onInputChange('adminUser.lastName', e.target.value)}
        error={errors['adminUser.lastName']}
        placeholder="Last name"
      />

      <div className="md:col-span-2">
        <Input
          label="Password *"
          type="password"
          value={formData.adminUser.password}
          onChange={(e) => onInputChange('adminUser.password', e.target.value)}
          error={errors['adminUser.password']}
          placeholder="Minimum 8 characters"
        />
      </div>
    </div>
  </div>
);

const PoliciesTab: React.FC<{
  formData: FormData;
  errors: Record<string, string>;
  onInputChange: (field: string, value: any) => void;
}> = ({ formData, errors, onInputChange }) => (
  <div className="p-6 space-y-6">
    <div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Correspondence Policies</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex items-center">
          <input
            id="auto-classification"
            type="checkbox"
            checked={formData.correspondencePolicy.autoClassificationEnabled}
            onChange={(e) => onInputChange('correspondencePolicy.autoClassificationEnabled', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="auto-classification" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
            Enable Auto Classification
          </label>
        </div>

        <div className="flex items-center">
          <input
            id="manual-review"
            type="checkbox"
            checked={formData.correspondencePolicy.requireManualReview}
            onChange={(e) => onInputChange('correspondencePolicy.requireManualReview', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="manual-review" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
            Require Manual Review
          </label>
        </div>

        <div>
          <label htmlFor="preservation-priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Preservation Priority
          </label>
          <select
            id="preservation-priority"
            value={formData.correspondencePolicy.preservationPriority}
            onChange={(e) => onInputChange('correspondencePolicy.preservationPriority', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <Input
          label="Max Attachment Size"
          type="text"
          value={formatBytes(formData.correspondencePolicy.maxAttachmentSize)}
          onChange={(e) => {
            const bytes = parseStorageValue(e.target.value);
            onInputChange('correspondencePolicy.maxAttachmentSize', bytes);
          }}
          error={errors['correspondencePolicy.maxAttachmentSize']}
          placeholder="e.g., 25 MB"
        />
      </div>
    </div>
  </div>
);

// ===== MAIN COMPONENT =====

export const OrganizationModal: React.FC<OrganizationModalProps> = memo(({ 
  isOpen, 
  onClose, 
  organization, 
  onSave, 
  loading = false 
}) => {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<TabType>('basic');

  const isEditing = !!organization;

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (organization) {
        // Editing existing organization
        setFormData({
          name: organization.name,
          description: organization.description || '',
          maxUsers: organization.maxUsers || 100,
          maxProjects: organization.maxProjects || 50,
          maxGroups: organization.maxGroups || 25,
          maxWorkflows: organization.maxWorkflows || 10,
          maxEmailAccounts: organization.maxEmailAccounts || 10,
          emailRetentionDays: organization.emailRetentionDays || 365,
          maxEmailStorage: organization.maxEmailStorage || 1000000000,
          maxCorrespondenceStorage: organization.maxCorrespondenceStorage || 1000000000,
          maxTotalStorage: organization.maxTotalStorage || 2000000000,
          emergencyPreservationEnabled: organization.emergencyPreservationEnabled || false,
          maxCorrespondences: organization.maxCorrespondences || 10000,
          plan: organization.plan || 'basic',
          isAIEnabled: organization.isAIEnabled || false,
          subscribedModules: organization.subscribedModules || [],
          isActive: organization.isActive !== false,
          adminUser: {
            username: '',
            email: '',
            firstName: '',
            lastName: '',
            password: ''
          },
          correspondencePolicy: {
            autoClassificationEnabled: true,
            requireManualReview: false,
            preservationPriority: 'medium',
            maxAttachmentSize: 25000000
          }
        });
      } else {
        // Reset to defaults for new organization
        setFormData(DEFAULT_FORM_DATA);
      }
      setErrors({});
      setActiveTab('basic');
    }
  }, [isOpen, organization]);

  const handleInputChange = useCallback((field: string, value: any) => {
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        const parentKey = parent as keyof FormData;
        const currentParentValue = prev[parentKey];
        
        // Type guard to ensure the parent value is an object
        if (typeof currentParentValue === 'object' && currentParentValue !== null && !Array.isArray(currentParentValue)) {
          return {
            ...prev,
            [parent]: {
              ...currentParentValue,
              [child]: value
            }
          };
        }
        
        // Fallback if parent is not an object (shouldn't happen in our case)
        console.warn(`Attempted to set nested field ${field} but parent ${parent} is not an object`);
        return prev;
      }
      return { ...prev, [field]: value };
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm(formData, isEditing);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Find which tab has the first error and navigate to it
      const errorFields = Object.keys(validationErrors);
      const firstError = errorFields[0];

      // Map fields to tabs
      if (firstError.includes('adminUser') && !isEditing) {
        setActiveTab('admin');
      } else if (['maxUsers', 'maxProjects', 'maxGroups', 'maxWorkflows'].includes(firstError)) {
        setActiveTab('resources');
      } else if (['maxEmailAccounts', 'emailRetentionDays', 'maxEmailStorage', 'maxCorrespondenceStorage', 'maxTotalStorage'].includes(firstError)) {
        setActiveTab('email');
      } else if (firstError.includes('correspondencePolicy')) {
        setActiveTab('policies');
      } else {
        setActiveTab('basic');
      }

      return;
    }

    // Prepare data for submission
    const submissionData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      maxUsers: formData.maxUsers,
      maxProjects: formData.maxProjects,
      maxGroups: formData.maxGroups,
      maxWorkflows: formData.maxWorkflows,
      maxEmailAccounts: formData.maxEmailAccounts,
      emailRetentionDays: formData.emailRetentionDays,
      maxEmailStorage: formData.maxEmailStorage,
      maxCorrespondenceStorage: formData.maxCorrespondenceStorage,
      maxTotalStorage: formData.maxTotalStorage,
      emergencyPreservationEnabled: formData.emergencyPreservationEnabled,
      maxCorrespondences: formData.maxCorrespondences,
      plan: formData.plan,
      isAIEnabled: formData.isAIEnabled,
      subscribedModules: formData.subscribedModules,
      isActive: formData.isActive,
      correspondencePolicy: formData.correspondencePolicy,
      ...(!isEditing && { adminUser: formData.adminUser })
    };

    onSave(submissionData);
  }, [formData, isEditing, onSave]);

  // Helper function to check if tab has errors
  const getTabErrors = useCallback((tabId: TabType): number => {
    const errorKeys = Object.keys(errors);
    let count = 0;

    switch (tabId) {
      case 'basic':
        if (errors.name || errors.description) count++;
        break;
      case 'resources':
        if (errors.maxUsers || errors.maxProjects || errors.maxGroups || errors.maxWorkflows) count++;
        break;
      case 'email':
        if (errors.maxEmailAccounts || errors.emailRetentionDays || errors.maxEmailStorage || errors.maxCorrespondenceStorage || errors.maxTotalStorage) count++;
        break;
      case 'admin':
        errorKeys.forEach(key => {
          if (key.includes('adminUser')) count++;
        });
        break;
      case 'policies':
        errorKeys.forEach(key => {
          if (key.includes('correspondencePolicy')) count++;
        });
        break;
    }

    return count;
  }, [errors]);

  const tabs = [
    { id: 'basic' as TabType, name: 'Basic Info' },
    { id: 'resources' as TabType, name: 'Resource Limits' },
    { id: 'email' as TabType, name: 'Email Settings' },
    ...(isEditing ? [] : [{ id: 'admin' as TabType, name: 'Admin User', required: true }]),
    { id: 'policies' as TabType, name: 'Policies' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Organization' : 'Create New Organization'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" role="tablist" aria-label="Organization settings">
            {tabs.map((tab) => {
              const tabErrors = getTabErrors(tab.id);
              const isRequired = (tab as any).required && !isEditing;

              return (
                <button
                  key={tab.id}
                  role="tab"
                  id={`tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`tabpanel-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2',
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  {tab.name}
                  {isRequired && (
                    <span className="text-red-500 text-xs" title="Required">*</span>
                  )}
                  {tabErrors > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-500 rounded-full">
                      {tabErrors}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Tab Content */}
          {activeTab === 'basic' && (
            <div role="tabpanel" id="tabpanel-basic" aria-labelledby="tab-basic">
              <BasicInfoTab formData={formData} errors={errors} onInputChange={handleInputChange} />
            </div>
          )}

          {activeTab === 'resources' && (
            <div role="tabpanel" id="tabpanel-resources" aria-labelledby="tab-resources">
              <ResourceLimitsTab formData={formData} errors={errors} onInputChange={handleInputChange} />
            </div>
          )}

          {activeTab === 'email' && (
            <div role="tabpanel" id="tabpanel-email" aria-labelledby="tab-email">
              <EmailSettingsTab formData={formData} errors={errors} onInputChange={handleInputChange} />
            </div>
          )}

          {activeTab === 'admin' && !isEditing && (
            <div role="tabpanel" id="tabpanel-admin" aria-labelledby="tab-admin">
              <AdminUserTab formData={formData} errors={errors} onInputChange={handleInputChange} />
            </div>
          )}

          {activeTab === 'policies' && (
            <div role="tabpanel" id="tabpanel-policies" aria-labelledby="tab-policies">
              <PoliciesTab formData={formData} errors={errors} onInputChange={handleInputChange} />
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2"
            >
              {loading && <LoadingSpinner size="sm" />}
              <span>{isEditing ? 'Update' : 'Create'} Organization</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
});

OrganizationModal.displayName = 'OrganizationModal';